/**
 * SprintViewContext — global view toggle (sprint / boy / all).
 *
 * Persists per-user via localStorage key `aip:sprint-view:<email>`.
 * Default: 'sprint'. Used by SprintBoyToggle, Daily, Pipeline, AccountList,
 * Leaderboard, etc.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getSession } from "@/lib/session";

export type SprintView = "sprint" | "boy" | "all";

interface SprintViewCtx {
  view: SprintView;
  setView: (v: SprintView) => void;
}

const Ctx = createContext<SprintViewCtx | null>(null);

function storageKey(): string {
  const s = getSession();
  return `aip:sprint-view:${s?.email ?? "anon"}`;
}

function readInitial(): SprintView {
  if (typeof window === "undefined") return "sprint";
  try {
    const v = window.localStorage.getItem(storageKey());
    if (v === "sprint" || v === "boy" || v === "all") return v;
  } catch { /* ignore */ }
  return "sprint";
}

export function SprintViewProvider({ children }: { children: ReactNode }) {
  const [view, setViewState] = useState<SprintView>(() => readInitial());

  useEffect(() => {
    try { window.localStorage.setItem(storageKey(), view); } catch { /* ignore */ }
  }, [view]);

  const value = useMemo<SprintViewCtx>(() => ({
    view,
    setView: (v) => setViewState(v),
  }), [view]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSprintView(): SprintViewCtx {
  const v = useContext(Ctx);
  if (v) return v;
  // Fallback when used outside provider — keeps components renderable.
  return { view: "sprint", setView: () => {} };
}
